import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Label } from './Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Sparkles, Loader2 } from 'lucide-react';
import { PersonaCategory, PersonaCategoryArrayValues } from '../helpers/schema';
import styles from './PersonaGenerateDialog.module.css';

interface PersonaGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (prompt: string, category?: PersonaCategory) => void;
  isGenerating?: boolean;
}

export const PersonaGenerateDialog: React.FC<PersonaGenerateDialogProps> = ({
  open,
  onOpenChange,
  onGenerate,
  isGenerating,
}) => {
  const [prompt, setPrompt] = useState('');
  const [category, setCategory] = useState<PersonaCategory | 'auto-detect'>('auto-detect');

  const categoryLabels: Record<string, string> = {
    development: 'Development',
    security: 'Security',
    operations: 'Operations',
    documentation: 'Documentation',
    compliance: 'Compliance',
    governance: 'Governance',
    custom: 'Custom',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.length < 10) return;
    onGenerate(prompt, category === 'auto-detect' ? undefined : category);
  };

  const examplePrompts = [
    'Create a persona for reviewing Kubernetes deployments and container security',
    'Build a GDPR compliance officer that checks data handling practices',
    'Design an API security expert focused on REST and GraphQL vulnerabilities',
    'Create a database performance specialist for SQL optimization',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialog}>
        <DialogHeader>
          <DialogTitle className={styles.titleRow}>
            <Sparkles size={20} className={styles.sparkle} />
            AI Persona Generator
          </DialogTitle>
          <DialogDescription>
            Describe the persona you want to create and AI will generate the rules and guidelines.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <Label htmlFor="prompt">Describe your persona</Label>
            <Textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., Create a security reviewer that focuses on authentication flaws, session management issues, and injection vulnerabilities..."
              required
              minLength={10}
              maxLength={2000}
              className={styles.textarea}
            />
            <span className={styles.charCount}>{prompt.length}/2000</span>
          </div>

          <div className={styles.field}>
            <Label htmlFor="category">Category (optional)</Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as PersonaCategory | 'auto-detect')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Auto-detect from description" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto-detect">Auto-detect</SelectItem>
                {PersonaCategoryArrayValues.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {categoryLabels[cat] || cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={styles.examples}>
            <Label>Example prompts</Label>
            <div className={styles.exampleList}>
              {examplePrompts.map((example, i) => (
                <button
                  key={i}
                  type="button"
                  className={styles.exampleButton}
                  onClick={() => setPrompt(example)}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isGenerating || prompt.length < 10}>
              {isGenerating ? (
                <>
                  <Loader2 size={14} className={styles.spinner} />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Generate Persona
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
